/**
 * Resolve a vendor from a supplier URL.
 *
 * Used by the Send-to-Ordering dialog so techs don't have to re-pick a
 * vendor that the part's URL already implies. Match precedence:
 *
 *   1. Known retailer hostname → canonical vendor name. Mirrors the
 *      hardcoded map in the backend's retail-option-paste flow
 *      (`core/services/retail_option_service.py:_RETAILER_NAMES`) so
 *      auto-created retailer vendor rows resolve the same way at send-
 *      time as they do at paste-time.
 *   2. Generic second-level-domain match. `leader.com.au` → SLD
 *      `leader` → vendor whose `code` or simplified `name` is
 *      `leader` (case-insensitive, alphanumerics only). Covers most
 *      disti hostnames without needing per-disti config.
 *
 * Returns `null` when the URL is missing, malformed, or no active
 * vendor matches.
 */
import type { Vendor } from '@techgitter/contracts';

// Mirrors the hardcoded retailer map on the backend. Keep in sync with
// `core/services/retail_option_service.py:_RETAILER_NAMES`.
const RETAILER_NAME_BY_HOSTNAME: Record<string, string> = {
  'officeworks.com.au': 'Officeworks',
  'jbhifi.com.au': 'JB Hi-Fi',
  'binglee.com.au': 'Bing Lee',
  'harveynorman.com.au': 'Harvey Norman',
  'thegoodguys.com.au': 'The Good Guys',
  'bigw.com.au': 'Big W',
  'kogan.com': 'Kogan',
  'amazon.com.au': 'Amazon',
  'centrecom.com.au': 'Centre Com',
  'scorptec.com.au': 'Scorptec',
  'umart.com.au': 'Umart',
  'msy.com.au': 'MSY',
  'mwave.com.au': 'Mwave',
  'pccasegear.com': 'PCCG',
};

/** Strip the leading "www." if present and lowercase. */
const normaliseHostname = (host: string): string =>
  host.toLowerCase().replace(/^www\./, '');

/** Reduce a string to lowercase alphanumerics so "JB Hi-Fi" / "jbhifi" / "jb_hi_fi" all collide. */
const simplify = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export const lookupVendorByUrl = (
  url: string | null | undefined,
  vendors: Vendor[],
): Vendor | null => {
  if (!url) return null;
  let hostname: string;
  try {
    hostname = normaliseHostname(new URL(url).hostname);
  } catch {
    return null;
  }
  if (!hostname) return null;

  const activeVendors = vendors.filter((v) => v.isActive);

  // Strategy 1 — known retailer hostname → canonical vendor name.
  const knownName = RETAILER_NAME_BY_HOSTNAME[hostname];
  if (knownName) {
    const target = simplify(knownName);
    const hit = activeVendors.find((v) => simplify(v.name) === target);
    if (hit) return hit;
  }

  // Strategy 2 — second-level domain matches vendor code or simplified name.
  // "leader.com.au" → "leader"; matches a vendor with code="LEADER" or
  // name="Leader Computers" (the simplified name starts with "leader…",
  // but we use a stricter equality on the simplified form to avoid
  // accidental hits like "leaderoftheband" — disti / retailer names are
  // typically short and identity-shaped).
  const sld = hostname.split('.')[0];
  if (sld) {
    const sldSimple = simplify(sld);
    const hit = activeVendors.find(
      (v) =>
        (v.code && v.code.toLowerCase() === sld) ||
        simplify(v.name) === sldSimple,
    );
    if (hit) return hit;
  }

  return null;
};
